export type ClassificationField = 'seniority' | 'seniority_level' | 'persona' | 'detailed_persona';

export type ClassificationOption = {
  id: string;
  label: string;
  deprecated?: boolean;
};

const CLASSIFICATION_FIELD_KEYS: ClassificationField[] = [
  'seniority',
  'seniority_level',
  'persona',
  'detailed_persona',
];

const normalizeKey = (raw: unknown) =>
  String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

const asId = (item: any) => {
  if (!item || typeof item !== 'object') return '';
  return String(
    item.id ?? item.attribute_id ?? item.vtr_id ?? item.uuid ?? item.attribute_master_id ?? '',
  ).trim();
};

const asLabel = (item: any, type?: unknown) => {
  if (item == null || item === '') return '';
  if (typeof item !== 'object') return String(item).trim();
  const typeKey = normalizeKey(type);
  const candidates = [
    item.attribute_value,
    item.value,
    item.label,
    item.display_name,
    item.name,
    item.attribute_name,
  ];
  for (const candidate of candidates) {
    const text = String(candidate ?? '').trim();
    if (text && normalizeKey(text) !== typeKey) return text;
  }
  return '';
};

const isDeprecated = (item: any) =>
  item?.is_deprecated === true || item?.is_deprecated === 'true' || item?.is_deprecated === 1;

const asRows = (response: any): any[] => {
  const candidates = [response, response?.data, response?.data?.data, response?.result];
  return candidates.find(Array.isArray) || [];
};

const emptyGroups = (): Record<ClassificationField, ClassificationOption[]> => ({
  seniority: [],
  seniority_level: [],
  persona: [],
  detailed_persona: [],
});

/** Group attribute-master search rows into the four person classification dropdowns. */
export function attributeOptionsFromMaster(
  response: any,
): Record<ClassificationField, ClassificationOption[]> {
  const grouped = emptyGroups();
  const push = (key: unknown, option: ClassificationOption) => {
    const field = normalizeKey(key) as ClassificationField;
    if (!grouped[field] || !option.id || !option.label) return;
    if (grouped[field].some(item => item.id === option.id)) return;
    grouped[field].push(option);
  };

  const payload = response?.data?.data ?? response?.data ?? response;
  if (payload && !Array.isArray(payload) && typeof payload === 'object') {
    Object.entries(payload).forEach(([key, value]) => {
      if (!Array.isArray(value)) return;
      value.forEach(item =>
        push(key, { id: asId(item), label: asLabel(item, key), deprecated: isDeprecated(item) }),
      );
    });
  }

  asRows(response).forEach(row => {
    if (!row || typeof row !== 'object') return;
    const type =
      row.attribute_type ??
      row.attribute_category ??
      row.category ??
      row.type ??
      row.field_name ??
      row.attribute_key ??
      row.master_type ??
      (row.attribute_value != null ? row.attribute_name : undefined) ??
      row.attribute;
    const values = row.values ?? row.attribute_values ?? row.options;
    if (type && Array.isArray(values)) {
      values.forEach(item =>
        push(type, { id: asId(item), label: asLabel(item, type), deprecated: isDeprecated(item) }),
      );
      return;
    }
    if (type) {
      push(type, { id: asId(row), label: asLabel(row, type), deprecated: isDeprecated(row) });
      return;
    }
    CLASSIFICATION_FIELD_KEYS.forEach(key => {
      const value = row[key];
      if (value == null || value === '') return;
      push(key, {
        id: typeof value === 'object' ? asId(value) : '',
        label: asLabel(value, key),
      });
    });
  });

  return grouped;
}
